import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../Auth-Context/AuthContext";
import { verifyTwoFactor } from "../api/services";
import { AlertCircle, ArrowLeft, Loader, Shield, Clock } from "lucide-react";
import ForgotPasswordModal from "../components/ForgotPasswordModal";
import { startSession } from "../utils/sessionCookie";

/* ── 2FA Verification Modal ── */
function TwoFactorModal({ open, onClose, onVerify, email, loading }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code || code.length < 6) {
      setError("Please enter a valid 6-digit code");
      return;
    }
    setError("");
    const success = await onVerify(code);
    if (!success) {
      setError("Invalid verification code. Please try again.");
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-md overflow-hidden bg-white shadow-2xl rounded-2xl">
        <div className="flex flex-col items-center px-6 pb-4 text-center pt-7">
          <div className="flex items-center justify-center mb-4 w-14 h-14 rounded-2xl bg-[#009DB9]/10">
            <Shield size={28} className="text-[#009DB9]" strokeWidth={1.5} />
          </div>
          <h2 className="text-[18px] font-bold text-neutral-900">Two-Factor Authentication</h2>
          <p className="text-[13px] text-neutral-500 mt-2">
            We've sent a verification code to <strong className="text-neutral-700">{email}</strong>
          </p>
          <p className="text-[11px] text-neutral-400 mt-1">Please enter the 6-digit code to continue</p>
        </div>

        <form onSubmit={handleSubmit} className="px-6 pb-6">
          <div className="mb-4">
            <input
              type="text"
              value={code}
              onChange={(e) => {
                setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                setError("");
              }}
              placeholder="000000"
              autoFocus
              className="w-full h-12 text-center text-2xl font-mono tracking-widest border border-neutral-200 rounded-xl px-4 outline-none focus:border-[#009DB9] transition-colors"
              style={{ letterSpacing: "0.5em" }}
            />
            {error && (
              <div className="flex items-center gap-1.5 mt-2 text-red-500 text-xs">
                <AlertCircle size={12} />
                <span>{error}</span>
              </div>
            )}
          </div>

          <div className="flex gap-2.5">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-10 rounded-xl text-[13px] font-semibold border border-neutral-200 text-neutral-600 hover:bg-neutral-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || code.length !== 6}
              className="flex-1 h-10 rounded-xl text-[13px] font-semibold text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ background: "#009DB9" }}
              onMouseEnter={(e) => { if (!loading && code.length === 6) e.currentTarget.style.background = "#007a90"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#009DB9"; }}
            >
              {loading ? <Loader size={14} className="animate-spin" /> : "Verify"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Session Expired Banner ── */
function SessionExpiredBanner({ onDismiss }) {
  return (
    <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 text-amber-700 text-xs rounded-lg px-3 py-2.5 mb-2">
      <Clock size={14} className="flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <span className="font-semibold">Your session has expired.</span>{" "}
        <span>Please sign in again to continue.</span>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="flex-shrink-0 text-amber-500 hover:text-amber-700"
        aria-label="Dismiss"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [tempUserData, setTempUserData] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const { login, setUser } = useAuth();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const [showExpiredBanner, setShowExpiredBanner] = useState(false);

  // Pick up ?reason=expired on mount, then strip it from the URL so a
  // manual refresh on the login page doesn't keep re-showing the banner.
  useEffect(() => {
    if (searchParams.get("reason") === "expired") {
      setShowExpiredBanner(true);
      const next = new URLSearchParams(searchParams);
      next.delete("reason");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Please fill in all fields.");
      return;
    }

    setShowExpiredBanner(false);
    setIsLoading(true);
    try {
      const response = await login(email, password);

      console.log("Login response:", response);

      if (response.success) {
        // Check if 2FA is required
        if (response.requiresTwoFactor) {
          console.log("2FA required, showing modal");
          setTempUserData({
            email: response.email || email,
            tempToken: response.tempToken,
            userId: response.userId
          });
          setShow2FA(true);
        } else {
          // Direct login - response.user contains the user data from AuthContext
          const userRole = response.user?.role;
          const originalRole = response.user?.originalRole;
          console.log("Direct login, user role:", userRole, "original role:", originalRole);

          // Check for SUPER_ADMIN, otherwise go to dashboard (including SALES_REP and EMPLOYEE)
          if (userRole === "SUPER_ADMIN") {
            navigate("/admin");
          } else {
            // SALES_REP, EMPLOYEE, ADMIN, MANAGER all go to dashboard
            navigate("/dashboard");
          }
        }
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err.message || "Login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify2FA = async (code) => {
    setIsVerifying(true);
    try {
      const response = await verifyTwoFactor({
        code: code,
        email: tempUserData?.email,
        tempToken: tempUserData?.tempToken
      });

      console.log("2FA verify response:", response);

      // Check for accessToken in response
      if (response.accessToken) {
        // Complete login - store user data
        let userData = {
          email: response.email,
          token: response.accessToken,
          role: response.role,
          name: `${response.firstName} ${response.lastName}`,
          id: response.userId,
          superAdmin: response.superAdmin,
          originalRole: response.role // Store original role
        };

        // Normalize role (EMPLOYEE -> SALES_REP)
        if (userData.role === "EMPLOYEE") {
          userData.role = "SALES_REP";
        }

        sessionStorage.setItem("crm_user", JSON.stringify(userData));
        // FIX: the 2FA path was never starting the 3h session cookie,
        // so isSessionExpired() would treat these users as immediately
        // expired on their very next refresh. Must match the direct-login
        // path in AuthContext.login().
        startSession();
        setUser(userData);

        setShow2FA(false);

        // Redirect based on user role (after normalization)
        console.log("2FA verified, user role:", userData.role, "original:", userData.originalRole);
        if (userData.role === "SUPER_ADMIN") {
          navigate("/admin");
        } else {
          navigate("/dashboard");
        }
        return true;
      } else {
        return false;
      }
    } catch (err) {
      console.error("2FA verification error:", err);
      return false;
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCancel2FA = () => {
    setShow2FA(false);
    setTempUserData(null);
  };

  const handleForgotPasswordClick = () => {
    setShowForgotPassword(true);
  };

  const handleCloseForgotPassword = () => {
    setShowForgotPassword(false);
  };

  const handleBackToLoginFromForgot = () => {
    setShowForgotPassword(false);
  };

  return (
    <>
      <div
        className="flex items-center justify-center w-screen h-screen overflow-hidden"
        style={{ background: "linear-gradient(135deg, #BDEAF2 0%, #EEF3FD 100%)" }}
      >
        {/* Card — 90vw wide, 85vh tall, never scrolls */}
        <div
          className="flex w-full overflow-hidden bg-white shadow-2xl rounded-3xl"
          style={{ width: "90vw", height: "85vh", maxWidth: "1200px" }}
        >
          {/* ── LEFT PANEL ── */}
          <div className="relative hidden h-full overflow-hidden md:block md:w-1/2">
            <img
              src="/login image.jpg"
              alt="CRM Illustration"
              className="absolute inset-0 object-contain w-full h-full"
            />

            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(to bottom, rgba(0,157,185,0.60) 0%, rgba(0,157,185,0.05) 45%, rgba(0,0,0,0.30) 100%)",
              }}
            />

            <div className="absolute top-0 left-0 z-10 p-8">
              <img
                src="/IZ-Logo.png"
                alt="IZ Sales CRM Logo"
                className="object-contain w-auto h-10"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "inline";
                }}
              />
              <span
                className="text-2xl font-bold tracking-tight text-white"
                style={{ display: "none" }}
              >
                IZ Sales
              </span>
            </div>

            <div className="absolute bottom-0 left-0 right-0 z-10 p-8">
              <p className="text-sm font-medium tracking-wide text-white drop-shadow">
                Manage customers · Track interactions · Drive sales
              </p>
            </div>
          </div>

          {/* ── RIGHT PANEL ── */}
          <div className="relative flex flex-col justify-center w-full px-10 md:w-1/2 lg:px-14">
            {/* Back to the public landing page */}
            <Link
              to="/"
              className="absolute top-6 left-10 lg:left-14 flex items-center gap-1.5 text-xs font-medium text-gray-500 transition-colors hover:text-[#009DB9]"
            >
              <ArrowLeft size={14} />
              Back to home
            </Link>

            {/* Logo on top of form - ONLY ADDED THIS LINE */}
            <div className="flex justify-center mb-6 md:hidden">
              <img
                src="/IZ-Logo.png"
                alt="IZ Sales CRM Logo"
                className="object-contain w-auto h-10"
              />
            </div>

            <div className="mb-8">
              <h1 className="mb-2 text-3xl font-bold tracking-tight text-gray-900">
                Welcome Back! 👋
              </h1>
              <p className="text-sm leading-relaxed text-gray-500">
                Sign in to manage customers, track interactions,
                <br className="hidden lg:block" /> and monitor sales activities.
              </p>
            </div>

            {showExpiredBanner && (
              <SessionExpiredBanner onDismiss={() => setShowExpiredBanner(false)} />
            )}

            <form onSubmit={handleSubmit} className="space-y-6" noValidate>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold tracking-widest text-gray-500 uppercase">
                  Email Address
                </label>
                <div className="flex items-center gap-3 border-b-2 border-gray-200 pb-2 focus-within:border-[#009DB9] transition-colors duration-200">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="flex-shrink-0"
                    style={{ color: email ? "#009DB9" : "#9ca3af" }}
                  >
                    <path
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    className="flex-1 bg-transparent outline-none text-gray-800 text-sm placeholder-gray-300 pb-0.5"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold tracking-widest text-gray-500 uppercase">
                  Password
                </label>
                <div className="flex items-center gap-3 border-b-2 border-gray-200 pb-2 focus-within:border-[#009DB9] transition-colors duration-200">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="flex-shrink-0"
                    style={{ color: password ? "#009DB9" : "#9ca3af" }}
                  >
                    <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M7 11V7a5 5 0 0110 0v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <circle cx="12" cy="16" r="1.5" fill="currentColor" />
                  </svg>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••••"
                    autoComplete="current-password"
                    className="flex-1 bg-transparent outline-none text-gray-800 text-sm placeholder-gray-300 pb-0.5"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="flex-shrink-0 text-gray-400 transition-colors hover:text-gray-600 focus:outline-none"
                    aria-label="Toggle password visibility"
                  >
                    {showPassword ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24M1 1l22 22"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <circle cx="12" cy="16" r="1.5" fill="currentColor" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="flex justify-end -mt-2">
                <button
                  type="button"
                  onClick={handleForgotPasswordClick}
                  className="text-xs font-medium transition-colors"
                  style={{ color: "#009DB9" }}
                  onMouseEnter={(e) => (e.target.style.color = "#007a90")}
                  onMouseLeave={(e) => (e.target.style.color = "#009DB9")}
                >
                  Forgot Password?
                </button>
              </div>

              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-100 text-red-600 text-xs rounded-lg px-3 py-2.5">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                    <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3.5 rounded-xl text-white font-semibold text-sm tracking-wide shadow-lg transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-[#009DB9]/30 disabled:opacity-70 disabled:cursor-not-allowed"
                style={{ background: isLoading ? "#6dcee0" : "#009DB9" }}
                onMouseEnter={(e) => { if (!isLoading) e.target.style.background = "#007a90"; }}
                onMouseLeave={(e) => { if (!isLoading) e.target.style.background = "#009DB9"; }}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3" strokeDasharray="31.4" strokeDashoffset="10" />
                    </svg>
                    Signing in…
                  </span>
                ) : (
                  "Sign In"
                )}
              </button>
            </form>

            <p className="mt-8 text-xs text-center text-gray-400">
              © {new Date().getFullYear()} IZ Sales CRM · All rights reserved
            </p>
          </div>
        </div>
      </div>

      {/* 2FA Verification Modal */}
      <TwoFactorModal
        open={show2FA}
        onClose={handleCancel2FA}
        onVerify={handleVerify2FA}
        email={tempUserData?.email || email}
        loading={isVerifying}
      />

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        open={showForgotPassword}
        onClose={handleCloseForgotPassword}
        onBackToLogin={handleBackToLoginFromForgot}
      />
    </>
  );
}